/**
 * loadRunIntoLiveDoc — project a backend run's drafted sections into the
 * live Word document.
 *
 * Used by:
 *   1. Auto-restore FALLBACK (useRunControl) — used only when the
 *      server-rendered .docx isn't available yet (a still running / paused run
 *      with no exported bundle → ``fetchRunDocument`` 404). For terminal runs
 *      the restore path now prefers ``hydrateLiveDocFromBase64`` (wordWriter),
 *      which inserts the canonical server .docx whole so the live doc is
 *      identical to the download by construction. A later cleanup removes this
 *      from the restore path once base64 soaks.
 *   2. Drafts panel "Load" button — lets the user explicitly swap the live
 *      doc to any prior run's content.
 *
 * Mirrors the live SSE path in ``useWordWriteQueue.handleProgressEvent``
 * exactly (sanitize → splitDraftBySections → insertSectionContent) so the
 * resulting body is byte-identical to a freshly-streamed run.
 */

import { type DocTypeConfig, getAllSectionContent } from "../../api/client";
import { splitDraftBySections } from "../../services/formatter/sectionSplitter";
import {
  insertSectionContent,
  scaffoldDocument,
  skeletonFromTuples,
  updateTitlePageFields,
} from "../../services/wordWriter";
import { beginLiveDocLoad, endLiveDocLoad } from "../../services/liveDocLoadState";
import { type ConfidentialityConfig, resolveConfidentiality } from "./confidentiality";
import { runPostSectionCitationPipeline } from "./citationPipeline";
import { sanitizeDraftForWord } from "./draftSanitizer";
import { hasTemplateCover, templateCoverScaffoldOverrides, writeTitlePage } from "./templateCover";
import { parseTitleFieldsFromMarkdown } from "./titleFieldsRecovery";

export interface LoadRunIntoLiveDocResult {
  sectionsWritten: number;
  /** How many section bodies we TRIED to write (``writeJobs.length``).
   *  ``sectionsAttempted > 0 && sectionsWritten === 0`` is the silent
   *  "headings-only" outcome — every body failed to find its heading. Callers
   *  must treat that as a failure, not a success (see ``loadRunIntoWord``). */
  sectionsAttempted: number;
  scaffolded: boolean;
  /** Human-readable cause when section numbers don't line up with the
   *  skeleton, e.g. "5 sections drafted, none matched a scaffold heading —
   *  9.1, 9.2 not in skeleton". Null when the namespaces agree. */
  orphanDetail?: string | null;
}

export interface LoadRunIntoLiveDocProgress {
  completed: number;
  total: number;
  percent: number;
}

export interface LoadRunIntoLiveDocOptions {
  onProgress?: (progress: LoadRunIntoLiveDocProgress) => void;
}

// Re-exported so existing importers of this module keep working; the
// implementation moved to ``confidentiality.ts`` so the live-projection
// scaffold in ``useWordWriteQueue`` can share it without importing this whole
// module (which pulls in the API client and wordWriter).
export { resolveConfidentiality };
export type { ConfidentialityConfig };

export async function loadRunIntoLiveDoc(
  runId: string,
  docConfig: DocTypeConfig,
  options?: LoadRunIntoLiveDocOptions,
): Promise<LoadRunIntoLiveDocResult> {
  console.log(`[loadRunIntoLiveDoc] starting runId=${runId}`);
  const sections = await getAllSectionContent(runId);
  console.log(`[loadRunIntoLiveDoc] getAllSectionContent returned sectionCount=${sections.length} runId=${runId}`);

  // Abort before wiping the doc when the server returned no content — this
  // happens when Redis TTL has expired AND the MongoDB cold-path returns an
  // empty response (e.g. run not found, or all sections have empty drafts).
  // Wiping the doc then writing nothing would silently erase the user's content.
  const hasContent = sections.some((s) => s.draft_content && s.draft_content.trim().length > 0);
  if (!hasContent) {
    console.log(
      `[loadRunIntoLiveDoc] ABORTED runId=${runId} reason=no_nonempty_sections sectionCount=${sections.length}`,
    );
    throw new Error(
      `loadRunIntoLiveDoc: server returned no drafted sections for run ${runId} — aborting to protect existing Word content`,
    );
  }

  // forceClear: session-refresh restore wipes the body before reinserting
  // skeleton — matches the user's "delete current live doc content" intent.
  const titlePage = docConfig.title_page as
    | {
        enabled?: boolean;
        title?: string | null;
        fields?: Array<{ label: string; fill: string } | string>;
        confidentiality?: ConfidentialityConfig;
        style?: import("../../services/wordWriter").TitlePageStyle;
        statement_fields?: string[];
      }
    | undefined;
  // Any TITLE section may carry title_fields; resolved before the scaffold,
  // which needs to know whether this run has a cover at all.
  const titleEntry = sections.find((s) => s.title_fields);
  let titleFields: Record<string, string> | null = titleEntry?.title_fields ?? null;
  // Fallback: no structured title_fields on any section. The drafting model may
  // have written the title page as ``**Label:** value`` PROSE and left
  // title_fields null (the flat agent loop never enforces the structured shape).
  // Every title-page sink renders ONLY from title_fields, so without this the
  // cover comes up blank on the per-section Load path even though the whole-file
  // "Load doc" path renders it (``_rebuild_docx_for_download`` recovers the same
  // way server-side). Recover here from the TITLE section's own body so the two
  // Load paths agree. A no-op when the server already supplied title_fields, so
  // real structured data is never overridden.
  if (!titleFields) {
    const titleSection = sections.find((s) => s.section_number === "TITLE" && s.draft_content);
    if (titleSection?.draft_content) {
      const recovered = parseTitleFieldsFromMarkdown(titleSection.draft_content);
      if (Object.keys(recovered).length > 0) {
        titleFields = recovered;
        console.info(
          `[loadRunIntoLiveDoc] recovered ${Object.keys(recovered).length} title field(s) from TITLE prose (title_fields was null)`,
        );
      }
    }
  }
  // Mark the live doc as loading/settling for the whole scaffold + per-section
  // replay so background re-projection (regulatory audit) stands down until the
  // rebuilt doc settles — otherwise a delete-then-reinsert could race the still-
  // settling body and blank a large region (the "blanks after Load" bug).
  beginLiveDocLoad();
  try {
  // scaffoldDocument is the only unguarded Office.js step on this path (the
  // per-section insert + citation passes below are already non-fatal). Attribute
  // any failure to the skeleton step so the surfaced error names the operation
  // instead of leaking a bare OfficeExtension.Error message.
  try {
    await scaffoldDocument({
      skeleton: skeletonFromTuples(docConfig.skeleton),
      // Prefer the doc-type's title_page.title banner (the SSOT the server
      // docx renderer uses via config.document_title) so both Load paths show
      // the same top-of-page title (e.g. "CLINICAL TRIAL PROTOCOL"); fall back
      // to the display_name for doc types that declare no banner.
      documentTitle: (titlePage?.title || docConfig.display_name)?.toUpperCase() || "",
      titlePageFields: titlePage?.fields,
      // Coerce the string-or-structured-mapping confidentiality to a literal
      // (or null = omit / undefined = default). Passing the raw mapping object
      // straight to Office.js insertParagraph is what threw InvalidArgument.
      confidentiality: resolveConfidentiality(titlePage?.confidentiality),
      forceClear: true,
      // A doc-type without a title page (CMC) gets no cover — unless this run
      // was drafted before it opted out and still holds its title fields.
      omitCover: titlePage?.enabled === false && !titleFields,
      // Official Word numbering on the per-section Load fallback too, so it
      // matches the whole-file hydrate path instead of diverging into
      // literal-string numbers (falls back to literal when the skeleton
      // isn't positionally representable). Suppressed entirely when the
      // doc-type opts out of heading numbers (heading_numbering: none).
      // "template" (private workspace templates): numbers exactly as
      // authored — literal text, title-only for unnumbered_sections.
      nativeNumbering: docConfig.heading_numbering !== "none" && docConfig.heading_numbering !== "template",
      suppressHeadingNumbers: docConfig.heading_numbering === "none",
      unnumberedSections: docConfig.heading_numbering === "template" ? (docConfig.unnumbered_sections ?? []) : [],
      // A template with its own title page: no standard chrome (see below).
      ...templateCoverScaffoldOverrides(titlePage),
      // The look the download renders with (a client template's own).
      styleSpec: docConfig.live_doc_style_spec,
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`loadRunIntoLiveDoc: scaffoldDocument failed for run ${runId} — ${detail}`);
  }

  // Applied after the scaffold so the resulting order matches the live path
  // (gate_start → section_ready → draft_ready title_fields).
  // The server-rendered title page (the download's own, fields filled), as the
  // whole-file Load shows it; the add-in's renderer only if the server can't.
  if (hasTemplateCover(titlePage) || titlePage?.enabled !== false || titleFields) {
    const fields = titleFields;
    try {
      await writeTitlePage(docConfig.document_type, fields ?? {}, async () => {
        if (fields && !hasTemplateCover(titlePage)) {
          await updateTitlePageFields(fields, titlePage?.style, titlePage?.statement_fields);
        }
      });
    } catch (err) {
      console.warn("[loadRunIntoLiveDoc] title page failed (non-fatal):", err);
    }
  }

  // Group sections by ``module_id`` so we can replay the same per-module
  // ``splitDraftBySections`` distribution the SSE handler does.
  const byModule = new Map<string, typeof sections>();
  for (const sec of sections) {
    if (!sec.section_number || sec.section_number === "TITLE") continue;
    const arr = byModule.get(sec.module_id) ?? [];
    arr.push(sec);
    byModule.set(sec.module_id, arr);
  }

  // A private workspace template's unnumbered headings render title-only, so
  // the number prefix can't find them; their skeleton title backs up the
  // bookmark. Empty for every public doctype.
  const unnumberedTitles = new Map<string, string>(
    docConfig.heading_numbering === "template"
      ? docConfig.skeleton
          .filter(([num]) => (docConfig.unnumbered_sections ?? []).includes(num))
          .map(([num, title]) => [num, title] as [string, string])
      : [],
  );
  // Every skeleton number: a module draft's heading for ANOTHER module's
  // section is dropped by the splitter instead of inlined under this one.
  const skeletonNumbers = (docConfig.skeleton ?? []).map(([num]) => String(num));
  const writeJobs: Array<{ secNum: string; chunk: string; secTitle?: string }> = [];
  for (const [, moduleSections] of byModule) {
    const allNums = moduleSections.map((s) => s.section_number).filter((n): n is string => !!n);
    for (const sec of moduleSections) {
      if (!sec.draft_content) continue;
      const cleaned = sanitizeDraftForWord(sec.draft_content);
      if (!cleaned) continue;
      const chunks = Object.entries(splitDraftBySections(cleaned, allNums, sec.section_number, skeletonNumbers)).filter(
        ([, c]) => c.trim().length > 0,
      );
      for (const [secNum, chunk] of chunks) {
        const secTitle = unnumberedTitles.get(secNum);
        writeJobs.push({ secNum, chunk, ...(secTitle ? { secTitle } : {}) });
      }
    }
  }

  // TEMP DIAGNOSTIC (Phase 0, 2026-06 headings-only restore bug): compare the
  // number namespaces. If writeJob secNums are NOT a subset of the skeleton
  // heading numbers, insertSectionContent will fail to find any heading and
  // the doc is left as a heading-only skeleton (hypothesis A). Remove once the
  // base64 restore path has shipped and the root cause is confirmed.
  let orphanDetail: string | null = null;
  try {
    const skeletonNums = skeletonFromTuples(docConfig.skeleton).map((h) => h.number);
    const skeletonSet = new Set(skeletonNums);
    const writeJobNums = writeJobs.map((j) => j.secNum);
    const orphanSecNums = writeJobNums.filter((n) => !skeletonSet.has(n));
    console.info("[loadRunIntoLiveDoc][phase0] number-namespace check", {
      runId,
      serverSectionNumbers: sections.map((s) => s.section_number),
      writeJobSecNums: writeJobNums,
      skeletonNumbers: skeletonNums,
      orphanSecNums,
      orphanCount: orphanSecNums.length,
      hypothesis:
        orphanSecNums.length === writeJobNums.length && writeJobNums.length > 0 ? "A_namespace_mismatch" : "not_A",
    });
    // Promote the diagnostic's conclusion onto the result so the Load error
    // card can name the real cause instead of a bare "nothing was written".
    if (orphanSecNums.length > 0) {
      const shown = orphanSecNums.slice(0, 6).join(", ");
      const more = orphanSecNums.length > 6 ? `, +${orphanSecNums.length - 6} more` : "";
      orphanDetail =
        `${orphanSecNums.length}/${writeJobNums.length} drafted sections have numbers that are ` +
        `not in the ${docConfig.document_type ?? "document"} skeleton (${shown}${more})`;
    }
  } catch (err) {
    console.warn("[loadRunIntoLiveDoc][phase0] diagnostic failed (non-fatal):", err);
  }

  let completed = 0;
  const total = writeJobs.length;
  const emitProgress = () => {
    const percent = total === 0 ? 100 : Math.round((completed / total) * 100);
    options?.onProgress?.({ completed, total, percent });
  };
  emitProgress();

  let written = 0;
  for (const { secNum, chunk, secTitle } of writeJobs) {
    try {
      const ok = await insertSectionContent({
        sectionNumber: secNum,
        sectionTitle: secTitle,
        content: chunk,
        scrollAfterInsert: false,
      });
      if (ok) {
        written += 1;
        // Wrap citation markers in the just-painted section so the
        // re-loaded doc behaves identically to a freshly-streamed
        // one. Without this the auto-restore path produced plain
        // ``[N]`` text that wasn't clickable (Bug #1 in the 2026-06
        // diagnosis). ``runPostSectionCitationPipeline`` swallows
        // its own errors; non-fatal on failure.
        await runPostSectionCitationPipeline(secNum);
      }
    } catch (err) {
      console.warn(`[loadRunIntoLiveDoc] insertSectionContent failed for §${secNum} (non-fatal):`, err);
    } finally {
      completed += 1;
      emitProgress();
    }
  }

  return { sectionsWritten: written, sectionsAttempted: total, scaffolded: true, orphanDetail };
  } finally {
    endLiveDocLoad();
  }
}
